import splitVocabularyTerms from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/constraint/splitVocabularyTerms';
import inheritedFromPhrase from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/inheritedFromPhrase';
import type UsageSettingView from '@UI/Store/AiConfiguration/Views/UsageSettingView';
import type { VocabularyBounds } from '@UI/Store/AiConfiguration/Views/UsageSettingsView';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { Switch, TextArea } from '@verclam/ui-kit';

const conflictBlockStyle = css`
	margin: 0;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

const counterStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	/** `formData.includesAccountOnlyFields`, threaded down rather than re-read from `scope.type`: the
	 * FormData already computed it once, in `fromView` — the only read of `scope.type` this lot adds. */
	readonly isAccountLevel: boolean;
	readonly setting: UsageSettingView<readonly string[], VocabularyBounds>;
	readonly isOverridden: boolean;
	readonly value: string;
	readonly onOverriddenChange: (overridden: boolean) => void;
	readonly onValueChange: (value: string) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
	readonly isSubmitting: boolean;
};

/**
 * `value` is the `TextArea`'s raw text (one term per line), never `string[]` — see
 * `UsageSettingsFormData` docblock. The counter below the active field reads `bounds.maxTerms`, always
 * a number, never `null`.
 */
const VocabularySettingField: React.FC<Props> = ({
	isAccountLevel,
	setting,
	isOverridden,
	value,
	onOverriddenChange,
	onValueChange,
	errorMessage,
	isFormFieldValid,
	isSubmitting,
}) => {
	const termCount = React.useMemo(() => splitVocabularyTerms(value).length, [value]);

	if (!setting.applicable) {
		return null;
	}

	if (!setting.editable) {
		return (
			<TextArea
				name="vocabularyTerms"
				label="Vocabulaire métier"
				value={(setting.effective ?? []).join('\n')}
				readOnly
				subLabel={inheritedFromPhrase(setting.effectiveLevel) ?? 'Aucun réglage d’appel.'}
			/>
		);
	}

	if (!setting.supported) {
		return (
			<React.Fragment>
				<TextArea
					name="vocabularyTerms"
					label="Vocabulaire métier"
					value={(setting.own ?? []).join('\n')}
					disabled
					subLabel={setting.unsupportedReason ?? undefined}
				/>
				{setting.isInConflict ? (
					<p css={conflictBlockStyle}>
						Ce réglage est enregistré mais le moteur affecté ne l’accepte pas. Il ne sera pas
						envoyé.
					</p>
				) : null}
			</React.Fragment>
		);
	}

	const showSwitch = !isAccountLevel;

	if (showSwitch && !isOverridden) {
		return (
			<React.Fragment>
				<Switch
					name="vocabularyTerms-override"
					label="Personnaliser ce réglage"
					checked={isOverridden}
					onChange={onOverriddenChange}
					disabled={isSubmitting}
				/>
				<TextArea
					name="vocabularyTerms"
					label="Vocabulaire métier"
					value={(setting.effective ?? []).join('\n')}
					readOnly
					subLabel={inheritedFromPhrase(setting.effectiveLevel) ?? 'Aucun réglage d’appel.'}
				/>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			{showSwitch ? (
				<Switch
					name="vocabularyTerms-override"
					label="Personnaliser ce réglage"
					checked={isOverridden}
					onChange={onOverriddenChange}
					disabled={isSubmitting}
				/>
			) : null}
			<TextArea
				name="vocabularyTerms"
				label="Vocabulaire métier"
				subLabel="Un terme par ligne."
				value={value}
				onChange={onValueChange}
				disabled={isSubmitting}
				errorMessage={errorMessage}
				isFormFieldValid={isFormFieldValid}
			/>
			<p css={counterStyle}>
				{termCount} / {setting.bounds.maxTerms} termes
			</p>
		</React.Fragment>
	);
};

export default VocabularySettingField;
